/*
 * @Author: lizhigang
 * @Date: 2025-12-16
 * @Company: www.smm.cn
 * @Description: 密码输入框 Hook - 禁止中文输入
 */

import React from "react"
import type { FormInstance } from "antd"

import { filterChinese } from "../utils"

interface UsePasswordInputOptions {
  form: FormInstance
  fieldName?: string
  onPasswordChange?: (value: string) => void
}

interface UsePasswordInputReturn {
  handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handlePasswordPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void
  handleCompositionStart: (e: React.CompositionEvent<HTMLInputElement>) => void
}

/**
 * 密码输入框自定义 Hook
 * 提供禁止中文输入、过滤粘贴内容中的中文字符等功能
 *
 * @param options - 配置选项
 * @param options.form - Ant Design Form 实例
 * @param options.fieldName - 表单字段名，默认为 "password"
 * @param options.onPasswordChange - 密码变化回调函数
 * @returns 密码输入框事件处理函数
 *
 * @example
 * ```tsx
 * const [form] = Form.useForm()
 * const [password, setPassword] = useState("")
 * const { handlePasswordChange, handlePasswordPaste, handleCompositionStart } = usePasswordInput({
 *   form,
 *   fieldName: "password",
 *   onPasswordChange: setPassword
 * })
 *
 * <Input.Password
 *   onChange={handlePasswordChange}
 *   onPaste={handlePasswordPaste}
 *   onCompositionStart={handleCompositionStart}
 * />
 * ```
 */
export const usePasswordInput = ({
  form,
  fieldName = "password",
  onPasswordChange,
}: UsePasswordInputOptions): UsePasswordInputReturn => {
  /**
   * 处理密码输入变化
   * 实时过滤中文字符
   */
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filteredValue = filterChinese(e.target.value)
    // 如果过滤后的值与原值不同，说明包含中文，需要更新表单值
    if (filteredValue !== e.target.value) {
      form.setFieldsValue({ [fieldName]: filteredValue })
    }
    onPasswordChange?.(filteredValue)
  }

  /**
   * 处理粘贴事件
   * 过滤粘贴内容中的中文字符
   */
  const handlePasswordPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData("text")
    const filteredText = filterChinese(pastedText)

    const input = e.target as HTMLInputElement
    const start = input.selectionStart || 0
    const end = input.selectionEnd || 0
    const currentValue = input.value

    // 构建新值：当前值的前半部分 + 过滤后的粘贴内容 + 当前值的后半部分
    const newValue = currentValue.slice(0, start) + filteredText + currentValue.slice(end)

    // 更新表单值
    form.setFieldsValue({ [fieldName]: newValue })
    onPasswordChange?.(newValue)

    // 设置光标位置
    setTimeout(() => {
      input.setSelectionRange(start + filteredText.length, start + filteredText.length)
    }, 0)
  }

  /**
   * 处理输入法组合开始事件
   * 阻止中文输入法的输入（在 Windows 系统上特别重要）
   */
  const handleCompositionStart = (e: React.CompositionEvent<HTMLInputElement>) => {
    e.preventDefault()
  }

  return {
    handlePasswordChange,
    handlePasswordPaste,
    handleCompositionStart,
  }
}
